// src/pages/workflow/AgentToolEditPage.tsx
// Create/edit an agent tool. Kind-specific sections: HTTP endpoint (url,
// method, headers, parameter schema, credential), platform RPC, knowledge.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import {
  Button,
  Input,
  Textarea,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { getEntityId } from '@/features/workflow/logic';
import {
  agentToolKindOptions,
  httpMethodOptions,
  emptyAgentToolForm,
  agentToolToForm,
  validateAgentToolForm,
  buildAgentToolPayload,
  type AgentToolFormData,
} from '@/features/workflow/aiConfig';
import type { AgentToolKind } from '@core/types/workflow/AgentToolKind';
import type { Credential } from '@core/types/workflow/Credential';

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    </svg>
  );
}

export const AgentToolEditPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const { toolId } = useParams<{ toolId: string }>();
  const isCreate = !toolId;

  const [formData, setFormData] = useState<AgentToolFormData>(emptyAgentToolForm());
  const [isBuiltin, setIsBuiltin] = useState(false);
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [loadFailed, setLoadFailed] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [credentials, setCredentials] = useState<Credential[]>([]);

  useEffect(() => {
    if (!toolId) return;
    let cancelled = false;
    setIsLoading(true);
    store
      .GetAgentTool(toolId)
      .then((res) => {
        if (cancelled) return;
        const tool = res?.agent_tool;
        if (tool) {
          setFormData(agentToolToForm(tool));
          setIsBuiltin(!!tool.is_builtin);
        } else {
          setLoadFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId]);

  // Credential picker options (HTTP tools reference workflow credentials).
  useEffect(() => {
    let cancelled = false;
    store
      .ListCredentials()
      .then((res) => {
        if (!cancelled) setCredentials(res?.items?.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readOnly = isBuiltin;

  const setField = <K extends keyof AgentToolFormData>(key: K, value: AgentToolFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key as string]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const setHeader = (index: number, patch: Partial<{ key: string; value: string }>) => {
    setFormData((prev) => ({
      ...prev,
      headers: prev.headers.map((h, i) => (i === index ? { ...h, ...patch } : h)),
    }));
  };

  const addHeader = () => {
    setFormData((prev) => ({ ...prev, headers: [...prev.headers, { key: '', value: '' }] }));
  };

  const removeHeader = (index: number) => {
    setFormData((prev) => ({ ...prev, headers: prev.headers.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    const errors = validateAgentToolForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = buildAgentToolPayload(formData, scope);
      if (!toolId) {
        await store.CreateAgentTool({ agent_tool: payload });
      } else {
        await store.UpdateAgentTool(toolId, { agent_tool: payload });
      }
      navigate('/workflows/agent-tools');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!toolId || readOnly) return;
    if (!confirm(t('workflow.deleteAgentToolConfirm'))) return;
    try {
      await store.DeleteAgentTool(toolId);
      navigate('/workflows/agent-tools');
    } catch {
      // Error handled by store
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="space-y-4">
        {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground mb-4">{t('workflow.agentToolNotFound')}</p>
            <Link to="/workflows/agent-tools">
              <Button variant="secondary">{t('common.back')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to="/workflows/agent-tools" className="text-muted-foreground hover:text-foreground">{t('workflow.agentTools')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{isCreate ? t('workflow.addAgentTool') : formData.name || formData.slug}</span>
          </nav>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              {isCreate ? t('workflow.addAgentTool') : formData.name}
            </h1>
            {isBuiltin && <Badge variant="secondary" size="sm">{t('workflow.builtin')}</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isCreate && (
            <span title={readOnly ? t('workflow.builtinReadOnly') : undefined}>
              <Button variant="ghost" size="sm" className="text-destructive" disabled={readOnly} onClick={handleDelete}>
                {t('common.delete')}
              </Button>
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={() => navigate('/workflows/agent-tools')}>
            {t('common.cancel')}
          </Button>
          <span title={readOnly ? t('workflow.builtinReadOnly') : undefined}>
            <Button size="sm" disabled={readOnly || isSubmitting} isLoading={isSubmitting} onClick={handleSave}>
              {t('common.save')}
            </Button>
          </span>
        </div>
      </div>

      {isBuiltin && (
        <div className="rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
          {t('workflow.builtinAgentToolHint')}
        </div>
      )}

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle>{t('workflow.general')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('workflow.field.name')}
              value={formData.name}
              onChange={(e) => setField('name', e.target.value)}
              error={formErrors.name ? t(formErrors.name) : undefined}
              disabled={readOnly}
              required
            />
            <Input
              label={t('workflow.field.slug')}
              value={formData.slug}
              onChange={(e) => setField('slug', e.target.value)}
              error={formErrors.slug ? t(formErrors.slug) : undefined}
              helperText={isCreate ? t('workflow.slugHint') : t('workflow.slugImmutable')}
              disabled={readOnly || !isCreate}
              className="font-mono"
              required
            />
          </div>
          <Textarea
            label={t('workflow.field.description')}
            value={formData.description}
            onChange={(e) => setField('description', e.target.value)}
            error={formErrors.description ? t(formErrors.description) : undefined}
            helperText={t('workflow.toolDescriptionHint')}
            rows={3}
            disabled={readOnly}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <Select
              label={t('workflow.field.kind')}
              value={formData.kind}
              onChange={(e) => setField('kind', e.target.value as AgentToolKind)}
              helperText={isCreate ? undefined : t('workflow.kindImmutable')}
              disabled={readOnly || !isCreate}
            >
              {agentToolKindOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </Select>
            <div className="pb-2">
              <ToggleSwitch
                label={t('workflow.field.isActive')}
                checked={formData.is_active}
                onChange={(checked) => setField('is_active', checked)}
                disabled={readOnly}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* HTTP */}
      {formData.kind === 'AGENT_TOOL_KIND_HTTP' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('workflow.toolKind.http')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-4">
              <Select
                label={t('workflow.field.httpMethod')}
                value={formData.http_method}
                onChange={(e) => setField('http_method', e.target.value)}
                disabled={readOnly}
              >
                {httpMethodOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </Select>
              <Input
                label={t('workflow.field.url')}
                value={formData.url}
                onChange={(e) => setField('url', e.target.value)}
                error={formErrors.url ? t(formErrors.url) : undefined}
                placeholder="https://api.example.com/v1/search"
                className="font-mono"
                disabled={readOnly}
                required
              />
            </div>

            {/* Headers */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-foreground">{t('workflow.field.headers')}</span>
                {!readOnly && (
                  <Button variant="secondary" size="sm" onClick={addHeader}>
                    {t('workflow.addHeader')}
                  </Button>
                )}
              </div>
              {formData.headers.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('workflow.noHeaders')}</p>
              ) : (
                <div className="space-y-2">
                  {formData.headers.map((h, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input
                        value={h.key}
                        onChange={(e) => setHeader(i, { key: e.target.value })}
                        placeholder={t('workflow.headerKeyPlaceholder')}
                        className="font-mono flex-1"
                        disabled={readOnly}
                      />
                      <Input
                        value={h.value}
                        onChange={(e) => setHeader(i, { value: e.target.value })}
                        placeholder={t('workflow.headerValuePlaceholder')}
                        className="font-mono flex-1"
                        disabled={readOnly}
                      />
                      {!readOnly && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeHeader(i)}>
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Textarea
              label={t('workflow.field.parametersSchema')}
              value={formData.parameters_schema}
              onChange={(e) => setField('parameters_schema', e.target.value)}
              error={formErrors.parameters_schema ? t(formErrors.parameters_schema) : undefined}
              helperText={t('workflow.parametersSchemaHint')}
              rows={8}
              className="font-mono"
              spellCheck={false}
              disabled={readOnly}
            />

            <Select
              label={t('workflow.field.credential')}
              value={formData.credential_id}
              onChange={(e) => setField('credential_id', e.target.value)}
              helperText={t('workflow.toolCredentialHint')}
              disabled={readOnly}
            >
              <option value="">{t('workflow.noCredential')}</option>
              {credentials.map((cred) => {
                const id = getEntityId(cred);
                return (
                  <option key={id} value={id}>{cred.name || id}</option>
                );
              })}
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Platform RPC */}
      {formData.kind === 'AGENT_TOOL_KIND_PLATFORM_RPC' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('workflow.toolKind.rpc')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              label={t('workflow.field.rpcMethod')}
              value={formData.rpc_method}
              onChange={(e) => setField('rpc_method', e.target.value)}
              error={formErrors.rpc_method ? t(formErrors.rpc_method) : undefined}
              placeholder="task.services.v1.TaskService/ListTasks"
              helperText={t('workflow.rpcMethodHint')}
              className="font-mono"
              disabled={readOnly}
              required
            />
          </CardContent>
        </Card>
      )}

      {/* Knowledge */}
      {formData.kind === 'AGENT_TOOL_KIND_KNOWLEDGE' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('workflow.toolKind.knowledge')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              label={t('workflow.field.knowledgeBase')}
              value={formData.knowledge_base_id}
              onChange={(e) => setField('knowledge_base_id', e.target.value)}
              helperText={t('workflow.knowledgeBaseHint')}
              className="font-mono"
              disabled={readOnly}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default AgentToolEditPage;
